import React, { useContext, useState } from 'react';
import { Form, Formik } from 'formik';
import { Modal, Button, Col, Row } from 'react-bootstrap';
import { LoruleRequestConfig, RoleUtils } from '@galaxy/lorule-react-commons';
import * as Yup from 'yup';
import { ResponseError, SubmitButton, FileField, TextAreaField } from '@nintendo/forms';
import { useMsal } from '@azure/msal-react';
import ConfigContext from '../../../contexts/ConfigContext';
import NinModal from '../../common/modal/NinModal';
import { ModalFormBaseProps } from '../../common/formTypes';
import { addAttachment, updateAttachment } from '../../../api/attachmentApi';
import { sendToast, truncateString } from '../../../utils/displayUtils';
import { Attachment } from '../../../api/actionMemoApiTypes';
import { AmStatusType, COMMENT_MAX_LENGTH } from '../../../utils/Constants';
import TextCountedField from '../../common/TextCountedField';

const FILE_SIZE = 20 * 1024 * 1024; // 20MB

export interface AttachmentModalProps extends ModalFormBaseProps {
    amId: number;
    amNumber: number;
    supNumber: number;
    amStatus: string;
    attachments: Attachment[];
}

const uploadAttachment = async (
    config: LoruleRequestConfig,
    values: {
        amId: number;
        amNumber: number;
        supNumber: number;
        description: string;
        commentMessage: string;
        uploadFile: File | null;
    },
    attachments: Attachment[],
    roles: string[],
    setResponseError: (errorMessage: string) => void,
    resetForm: () => void,
    setSubmitting: (isSubmitting: boolean) => void,
    toggleModal: () => void,
) => {
    try {
        if (values.uploadFile) {
            const oldAttachment = attachments.find(
                (attachment) =>
                    values.uploadFile && attachment.attachmentName === values.uploadFile.name,
            );
            if (oldAttachment) {
                await updateAttachment(
                    config,
                    values.amId,
                    oldAttachment.attachmentId,
                    values.description,
                    values.commentMessage,
                    values.uploadFile,
                    !RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
                );

                sendToast(
                    <>
                        Updated Attachment:{' '}
                        <strong>{truncateString(values.uploadFile.name)}</strong>
                    </>,
                );
            } else {
                await addAttachment(
                    config,
                    values.amId,
                    values.description,
                    values.commentMessage,
                    values.uploadFile,
                    !RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
                );

                sendToast(
                    <>
                        Added Attachment: <strong>{truncateString(values.uploadFile.name)}</strong>
                    </>,
                );
            }
        }

        resetForm();
        toggleModal();

        return true;
    } catch (error) {
        if (error instanceof Error) {
            setResponseError(`Attachment failed:  ${error.message}`);
        }
        setSubmitting(false);
        return false;
    }
};

const AttachmentModal = (props: AttachmentModalProps) => {
    const { instance } = useMsal();
    const config = useContext(ConfigContext);
    const [responseError, setResponseError] = useState('');
    const roles = RoleUtils.getRoles(instance);

    const validationSchema = Yup.object().shape({
        uploadFile: Yup.mixed()
            .required('A file is required')
            .test(
                'fileSize',
                'Attachment cannot be over 20MB',
                (value) => value && value.size <= FILE_SIZE,
            ),
        description: Yup.string()
            // eslint-disable-next-line no-template-curly-in-string
            .max(200, 'Description exceeds max length of ${max} characters')
            .matches(
                /^[\x20-\x7F]*$/,
                'Description must consist of only ASCII characters (check for MS Word hyphenation)',
            ),
        commentMessage: Yup.string()
            // eslint-disable-next-line no-template-curly-in-string
            .max(COMMENT_MAX_LENGTH, 'Comment exceeds max length of ${max} characters')
            .test(
                'required-check',
                'Comment is required',
                (commentMessage) => props.amStatus === AmStatusType.Draft || !!commentMessage,
            ),
    });

    const initialValues = {
        amId: props.amId,
        amNumber: props.amNumber,
        supNumber: props.supNumber,
        description: '',
        commentMessage: '',
        uploadFile: null,
    };

    return (
        <NinModal
            size="lg"
            button={props.toggleButton}
            render={(toggleModal: () => void) => (
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={async (values, { resetForm, setSubmitting }) => {
                        const submitSuccessful = await uploadAttachment(
                            config,
                            values,
                            props.attachments,
                            roles,
                            setResponseError,
                            resetForm,
                            setSubmitting,
                            toggleModal,
                        );
                        if (props.onSubmit && submitSuccessful) {
                            setResponseError('');
                            props.onSubmit();
                        }
                    }}
                    render={({
                        isSubmitting,
                        values,
                        touched,
                        errors,
                        setFieldValue,
                        setFieldTouched,
                        dirty,
                    }) => (
                        <>
                            <Modal.Header closeButton>
                                <Modal.Title>Upload Attachment</Modal.Title>
                            </Modal.Header>
                            <Form>
                                <Modal.Body>
                                    <ResponseError errorMessage={responseError} />

                                    <FileField
                                        dropZoneText="click here to pick file to upload"
                                        label={`Upload Attachment: 
                                           (txt/csv/jpeg/png/doc/docx/msg/
                                            xls/xlsm/xlxs/ppt/pptx/pdf)`}
                                        name="uploadFile"
                                        touched={touched.uploadFile}
                                        errors={errors.uploadFile}
                                        isRequired
                                        onChange={(file?: File) => {
                                            setFieldTouched('uploadFile', true);
                                            if (file) {
                                                setFieldValue('uploadFile', file);
                                            }
                                        }}
                                        acceptTypes={{
                                            'text/csv': ['.csv'],
                                            'text/plain': ['.txt'],
                                            'image/jpeg': ['.jpeg', '.jpg'],
                                            'image/png': ['.png'],
                                            'application/pdf': ['.pdf'],
                                            'application/msword': ['.doc'],
                                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                                                ['.docx'],
                                            'application/vnd.ms-excel': ['.xls'],
                                            'application/vnd.ms-excel.sheet.macroEnabled.12': [
                                                '.xlsm',
                                            ],
                                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                                                ['.xlsx'],
                                            'application/vnd.ms-powerpoint': ['.ppt'],
                                            'application/vnd.openxmlformats-officedocument.presentationml.presentation':
                                                ['.pptx'],
                                            'application/vnd.ms-outlook': ['.msg'],
                                        }}
                                        dropZoneOptions={{ noDrag: false }}
                                        testId="upload-file"
                                    />
                                    <Row>
                                        <Col sm="5" />
                                        <Col sm="7" className="mb-4">
                                            <small className="form-text text-muted document-limit-text">
                                                Attachment cannot be over 20MB
                                            </small>
                                        </Col>
                                    </Row>

                                    <TextAreaField
                                        label="Description"
                                        name="description"
                                        touched={touched.description}
                                        errors={errors.description}
                                        testId="description"
                                    />
                                    {(props.amStatus === AmStatusType.Pending ||
                                        props.amStatus === AmStatusType.Hold ||
                                        props.amStatus === AmStatusType.Approved) && (
                                        <TextCountedField
                                            label="Comment"
                                            name="commentMessage"
                                            touched={touched.commentMessage}
                                            errors={errors.commentMessage}
                                            isRequired
                                            testId="commentMessage"
                                            length={
                                                values.commentMessage
                                                    ? values.commentMessage.length
                                                    : 0
                                            }
                                            maxLength={COMMENT_MAX_LENGTH}
                                        />
                                    )}
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={toggleModal}
                                        disabled={isSubmitting}
                                    >
                                        Close
                                    </Button>
                                    <SubmitButton
                                        className="btn-primary ml-auto"
                                        disabled={!dirty}
                                        isSubmitting={isSubmitting}
                                        buttonText="Upload"
                                    />
                                </Modal.Footer>
                            </Form>
                        </>
                    )}
                />
            )}
        />
    );
};

export default AttachmentModal;
